/**
 * A bounded LRU with a TTL.
 *
 * The reason it exists: private channels are **at-least-once** (docs/04 §9.4).
 * `orders`, `positions` and `balance` dedupe on `version` and `fills`,
 * `settlements` and `notifications` on their own immutable ids — but a redelivery
 * that arrives after the client has already retired the row has nothing left to
 * compare against. The 10-minute `event_id` LRU is the belt-and-braces layer
 * under that: if we have seen this `event_id` recently, it is a redelivery, full
 * stop.
 *
 * Bounded *and* expiring, because either alone is wrong. Unbounded leaks across
 * a session that runs for hours at 2,880 markets a day; unexpiring keeps a
 * decision alive long after the thing it decided about is gone.
 *
 * **The clock is injected.** `Date.now()` lives only in `lib/clock/` (D9), and
 * an LRU driven by a wall clock a user can change is an LRU that can be made to
 * forget on demand. Pass the monotonic reading — `performance.now()` or the
 * corrected server clock — and elapsed time means elapsed time.
 */

export interface TtlLruOptions {
  /** Entries retained before the least-recently-used is evicted. */
  max: number
  /** Lifetime of an entry, in the same units `now` returns. */
  ttlMs: number
  /** A monotonic clock. Required — there is no safe default here. */
  now: () => number
}

interface Entry<V> {
  value: V
  expiresAt: number
}

export class TtlLru<K, V> {
  readonly #max: number
  readonly #ttlMs: number
  readonly #now: () => number
  // Insertion-ordered, so the first key is always the least recently used.
  readonly #map = new Map<K, Entry<V>>()

  #hits = 0
  #misses = 0
  #evictions = 0
  #expirations = 0

  constructor(options: TtlLruOptions) {
    if (!Number.isFinite(options.max) || options.max <= 0) throw new Error('max must be a positive number')
    if (!Number.isFinite(options.ttlMs) || options.ttlMs <= 0) throw new Error('ttlMs must be a positive number')
    if (typeof options.now !== 'function') {
      throw new Error('TtlLru needs an explicit clock: Date.now() is confined to lib/clock (D9)')
    }
    this.#max = options.max
    this.#ttlMs = options.ttlMs
    this.#now = options.now
  }

  get size(): number {
    return this.#map.size
  }

  get stats(): { hits: number; misses: number; evictions: number; expirations: number } {
    return {
      hits: this.#hits,
      misses: this.#misses,
      evictions: this.#evictions,
      expirations: this.#expirations,
    }
  }

  get(key: K): V | undefined {
    const entry = this.#map.get(key)
    if (!entry) {
      this.#misses++
      return undefined
    }
    if (entry.expiresAt <= this.#now()) {
      this.#map.delete(key)
      this.#expirations++
      this.#misses++
      return undefined
    }
    // Re-insert to move it to the most-recent end.
    this.#map.delete(key)
    this.#map.set(key, entry)
    this.#hits++
    return entry.value
  }

  has(key: K): boolean {
    return this.get(key) !== undefined
  }

  set(key: K, value: V): void {
    if (this.#map.has(key)) this.#map.delete(key)
    this.#map.set(key, { value, expiresAt: this.#now() + this.#ttlMs })
    this.#evictOverflow()
  }

  /**
   * Record `key` and report whether it was already present — the dedupe
   * primitive, as one operation, because a `has` followed by a `set` is two
   * chances to get the order wrong at the one call site that matters.
   *
   * Returns `true` the first time a key is seen, `false` for a redelivery.
   */
  add(key: K, value: V): boolean {
    const seen = this.get(key) !== undefined
    this.set(key, value)
    return !seen
  }

  delete(key: K): boolean {
    return this.#map.delete(key)
  }

  clear(): void {
    this.#map.clear()
  }

  /** Drop everything already expired. Optional — `get` expires lazily — but
   *  useful to call on a reconnect, when the client is reconciling anyway. */
  prune(): number {
    const now = this.#now()
    let removed = 0
    for (const [key, entry] of this.#map) {
      if (entry.expiresAt <= now) {
        this.#map.delete(key)
        removed++
      }
    }
    this.#expirations += removed
    return removed
  }

  #evictOverflow(): void {
    while (this.#map.size > this.#max) {
      const oldest = this.#map.keys().next()
      if (oldest.done) return
      this.#map.delete(oldest.value)
      this.#evictions++
    }
  }
}

/** The `event_id` window from docs/03 §4.4. */
export const EVENT_ID_TTL_MS = 10 * 60 * 1000

export function createEventIdLru(now: () => number, max = 20_000): TtlLru<string, true> {
  return new TtlLru<string, true>({ max, ttlMs: EVENT_ID_TTL_MS, now })
}
